import { Matrix4, Vector3 } from 'three';

import { ElementObj } from '../elements/element-obj';
import { TerrainModel } from '../elements/terrain-model';
import {
  CellGridVolumeModel,
  cutColorBarPalette,
  fillColorBarPalette,
  volumeColors,
} from '../elements/volume/model/cell-grid-volume-model';
import { PrismVolume } from '../elements/volume/prism-volume';
import { VolumeHandler } from '../elements/volume/volume-handler';
import { HexColor, VolumeColoring, VolumeLayer } from './manifest';

/**
 * A color of a shader as it is shown: its components are written out as they are, without a
 * conversion from linear.
 */
const toShaderHex = (rgb: number[]): HexColor =>
  `#${rgb
    .map((component) =>
      Math.round(component * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;

/** How the cell grid colors its cells, as its shader does. */
function getCellGridColoring(model: CellGridVolumeModel): VolumeColoring {
  // Without heights the viewer would show every cell in the outermost band, which isn't copied.
  if (model.displayGradient && model.containsHeightData) {
    return {
      mode: 'gradient',
      cutThresholds: [...model.cut_opacity_threshold_table],
      fillThresholds: [...model.fill_opacity_threshold_table],
      cutPalette: [...cutColorBarPalette],
      fillPalette: [...fillColorBarPalette],
    };
  }

  return {
    mode: 'slope',
    cut: { base: toShaderHex(volumeColors.cut.base), top: toShaderHex(volumeColors.cut.top) },
    fill: { base: toShaderHex(volumeColors.fill.base), top: toShaderHex(volumeColors.fill.top) },
  };
}

/**
 * Describes the cut and fill volume the asset shows, if any, by its ids, as it is shown: its parts,
 * opacity, colors and placement, from its cells or glb scene to world, without the model's raise,
 * which is sent on its own: it decides what the volume hides.
 */
export function collectVolumeLayer(
  asset: ElementObj,
  id: string,
  origo: Vector3,
): VolumeLayer | undefined {
  const handler = (asset as Partial<{ volumeHandler: VolumeHandler }>).volumeHandler;
  const model = handler?.volumeModel;
  const volume = handler?.activeVolume;
  if (!model?.visible || !model.parent || !volume?.id) {
    return undefined;
  }

  const calculatedOn =
    asset instanceof TerrainModel ? { geodataId: asset.geoDataId } : { assetId: asset.assetId };
  if (!calculatedOn.geodataId && !calculatedOn.assetId) {
    return undefined;
  }

  // The raise is on the model's children, so the model's own placement leaves it out.
  model.updateWorldMatrix(true, false);
  const matrix = new Matrix4().makeTranslation(origo).multiply(model.matrixWorld);

  return {
    id,
    name: asset.name || undefined,
    kind: 'volume',
    volumeType: volume instanceof PrismVolume ? 'prism' : 'cellGrid',
    volumeId: volume.id,
    ...calculatedOn,
    opacity: model.opacity,
    cut: model.cutVisibleState,
    fill: model.fillVisibleState,
    ...(model instanceof CellGridVolumeModel ? { coloring: getCellGridColoring(model) } : {}),
    matrix: matrix.toArray(),
    raise: model.raise,
  };
}
